import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import authService from '../services/auth';
import bagianService from '../services/bagian';
import { Activity, Eye, EyeOff, AlertCircle, Loader2, UserPlus, User, Lock, Building2, ChevronRight, CheckCircle, LogIn } from 'lucide-react';

const RegisterPage = ({ onSwitchToLogin }) => {
  const { theme } = useTheme();

  const registerBgStyle = {
    background: `linear-gradient(135deg, var(--theme-login-from), var(--theme-login-via), var(--theme-login-to))`,
  };

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    bagian: 'Umum',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bagianList, setBagianList] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchBagian = async () => {
      try {
        const data = await bagianService.getPublicBagian();
        if (data && data.bagian) {
          setBagianList(data.bagian);
        }
      } catch (err) {
        // Fallback to default bagian list
        setBagianList([
          { name: 'IT' },
          { name: 'Keuangan' },
          { name: 'HRD' },
          { name: 'Operasional' },
          { name: 'Marketing' },
          { name: 'Produksi' },
          { name: 'Umum' },
          { name: 'Kasir' },
        ]);
      }
    };
    fetchBagian();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    setError('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username wajib diisi';
    } else if (formData.username.trim().length < 3) {
      errors.username = 'Username minimal 3 karakter';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      errors.username = 'Username hanya boleh huruf, angka, dan underscore';
    }

    if (!formData.password) {
      errors.password = 'Password wajib diisi';
    } else if (formData.password.length < 6) {
      errors.password = 'Password minimal 6 karakter';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Konfirmasi password wajib diisi';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Password tidak cocok';
    }

    if (!formData.name.trim()) {
      errors.name = 'Nama lengkap wajib diisi';
    }

    if (!formData.bagian) {
      errors.bagian = 'Bagian wajib dipilih';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      await authService.registerPublic(
        formData.username.trim(),
        formData.password,
        formData.name.trim(),
        formData.bagian
      );
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Success view
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={registerBgStyle}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        </div>
        <div className="w-full max-w-md relative animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Pendaftaran Berhasil!</h2>
            <p className="text-gray-500 mb-6">
              Akun <span className="font-semibold text-gray-700">{formData.username}</span> berhasil dibuat.
              Silakan login untuk melanjutkan.
            </p>
            <button
              onClick={onSwitchToLogin}
              className="w-full py-2.5 btn-primary justify-center shadow-lg"
            >
              <LogIn className="w-5 h-5" />
              <span>Lanjut ke Login</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={registerBgStyle}>
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-3">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Buat Akun Baru</h1>
          <p className="text-green-700/70 mt-1.5">Daftar untuk mulai menggunakan Activity Tracker</p>
        </div>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Form Pendaftaran</h2>

          {/* Error Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2 animate-slide-up">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Nama Lengkap */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nama Lengkap <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-9 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all bg-gray-50 hover:bg-white focus:bg-white text-sm ${
                    formErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Masukkan nama lengkap"
                  autoComplete="name"
                  autoFocus
                />
              </div>
              {formErrors.name && (
                <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>
              )}
            </div>

            {/* Username */}
            <div>
              <label htmlFor="reg-username" className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  id="reg-username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full pl-9 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all bg-gray-50 hover:bg-white focus:bg-white text-sm ${
                    formErrors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Masukkan username"
                  autoComplete="username"
                />
              </div>
              {formErrors.username && (
                <p className="mt-1 text-xs text-red-500">{formErrors.username}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reg-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 pr-9 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all bg-gray-50 hover:bg-white focus:bg-white text-sm ${
                    formErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Minimal 6 karakter"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="mt-1 text-xs text-red-500">{formErrors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                Konfirmasi Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reg-confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 pr-9 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all bg-gray-50 hover:bg-white focus:bg-white text-sm ${
                    formErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ketik ulang password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Bagian */}
            <div>
              <label htmlFor="reg-bagian" className="block text-sm font-medium text-gray-700 mb-1">
                Bagian <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="w-4 h-4 text-gray-400" />
                </div>
                <select
                  id="reg-bagian"
                  name="bagian"
                  value={formData.bagian}
                  onChange={handleChange}
                  className={`w-full pl-9 pr-8 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all bg-gray-50 hover:bg-white focus:bg-white text-sm appearance-none ${
                    formErrors.bagian ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                >
                  <option value="">Pilih Bagian</option>
                  {bagianList.map((bg) => (
                    <option key={bg._id || bg.name} value={bg.name}>
                      {bg.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronRight className="w-4 h-4 text-gray-400 rotate-90" />
                </div>
              </div>
              {formErrors.bagian && (
                <p className="mt-1 text-xs text-red-500">{formErrors.bagian}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 btn-primary justify-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              style={{ boxShadow: `0 10px 15px -3px ${theme.colors.primary}40, 0 4px 6px -4px ${theme.colors.primary}40` }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Mendaftarkan...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  <span>Daftar Akun</span>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500">
              Sudah punya akun?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="font-semibold text-green-600 hover:text-green-700 transition-colors"
              >
                Login di sini
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-green-700/50 text-sm mt-5">
          Activity Management System v1.0
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
